import * as Safe from "safe-portals";
import { makeActionTypeDefinition } from "@flow/common/action_type";
import { Serializers } from "@flow/common/types";
import { CampaignTargetProviderConfigSerializer } from "@flow/common/campaign_target_provider";
import { thankYouEmailCustomConfig } from "@flow/core/shared/common_custom_config";

export const DecisionNotificationConfigSerializer = Safe.obj({
  enabled: Safe.bool,
  threshold: Safe.int,
  cc_emails: Safe.optional(Safe.array(Serializers.emailAddress)),
  subject_line: Safe.str,
  email_body: Safe.str,
  from_name: Safe.str,
  from_email: Safe.str,
});

/**
 * Represents all the values the admin user can set to configure a PetitionAction.
 * This is NOT the same as the values that are passed to the member facing PetitionActionComponent.
 */
export const ActionConfigSerializer = Safe.obj({
  // here you add action properties that can be edited when creating a journey
  // they are passed to ../frontend/admin/EditPetitionAction
  title: Safe.str,
  page_content: Safe.str,
  decision_target: Safe.optional(CampaignTargetProviderConfigSerializer),
  sign_button_label: Safe.str,
  recent_signature_label: Safe.str,
  image_url: Safe.str,
  image_alt_text: Safe.str,
  additional_signatures_count: Safe.int,
  enable_one_click_sign: Safe.bool,
  decision_notifications: DecisionNotificationConfigSerializer,
  comments_enabled: Safe.bool,
  ...thankYouEmailCustomConfig,
  custom_consent_configs: Safe.optional(Safe.array(Safe.uuid)),
  // if true, then supporters should see a sig count that totals all petitions within
  // this journey.
  merge_all_petition_counters: Safe.optional(Safe.bool),
});

/**
 * This data is passed to ../frontend/member/PetitionActionComponent when this action is begun
 *  it is generated in ../backend/action_type_handlers
 */
const ActionFrontendDataSerializer = Safe.obj({
  title: Safe.str,
  petition_signers: Safe.int,
  page_content: Safe.str,
  decision_target: Safe.optional(CampaignTargetProviderConfigSerializer),
  sign_button_label: Safe.str,
  recent_signature_label: Safe.str,
  from_name: Safe.str,
  consent_configs: Safe.array(Serializers.consentConfig),
  image_url: Safe.str,
  image_alt_text: Safe.str,
  additional_signatures_count: Safe.int,
  enable_one_click_sign: Safe.bool,
  comments_enabled: Safe.bool,
});

const petitionConfigDefaults = {
  title: "Campaign for XYZ",
  decision_target: undefined,
  page_content: "<h2>Petition text</h2><br /><h2>Why is this important?</h2><br />",
  sign_button_label: "Sign!",
  recent_signature_label: "signed this petition",
  from_name: "",
  image_url: "https://files.38degrees.org.uk/items/files/000/003/413/original/shutterstock_663459544.jpg",
  image_alt_text: "Parliament",
  additional_signatures_count: 0,
  thank_you_email_subject: "Thanks for signing - one more thing",
  thank_you_email_body: `
    <p>If you’re not already opted-in to emails and want to know the next steps on how we can win this campaign and others - <a href="https://38d.gs/join-38-now">click here to join 38 Degrees</a> - otherwise read on.</p>
    <p>The more people who get involved, the harder we'll be to ignore. Please can you share the campaign with your friends or family now.</p>
    <p>If you're on social media you can share the campaign on Facebook or Twitter, or there's an email below that you can forward on:</p>
    `,
  thank_you_email_footer: "",
  enable_one_click_sign: false,
  decision_notifications: {
    enabled: false,
    threshold: 1000,
    cc_emails: [],
    subject_line: "",
    email_body: "",
    from_name: "Megan",
    from_email: "action@38degrees.org.uk",
  },
  comments_enabled: false,
  custom_consent_configs: undefined,
  merge_all_petition_counters: false,
};

export const PetitionAction_def = makeActionTypeDefinition({
  plugin: "petition",
  // action type identifier. don't change this without migrating data
  action_type: "petition:PetitionAction",
  supports_a_b_tests: true,
  custom_config_type: ActionConfigSerializer,
  // these are the default action properties when a new PetitionAction is created
  custom_config_defaults: petitionConfigDefaults,
  custom_data_type: ActionFrontendDataSerializer,
  check_valid: checkPetitionConfigValid,
});

function checkPetitionConfigValid(config: ActionConfig) {
  const errors: Record<string, string> = {};
  // Petition config validation
  if (config.title.length === 0) {
    errors.title = "Title is required";
  }
  if (config.image_url.length === 0) {
    errors.image_url = "Image is required";
  }
  if (config.image_url.length > 0 && config.image_alt_text.length === 0) {
    errors.image_alt_text = "Image alt text is required";
  }
  if (config.sign_button_label.length === 0) {
    errors.sign_button_label = "Sign button text is required";
  }
  if (config.recent_signature_label.length === 0) {
    errors.recent_signature_label = "Recent signatures text is required";
  }
  // Thank you email validation
  if (config.from_name.length === 0) {
    errors.from_name = "Thank you email from name is required";
  }
  if (config.thank_you_email_subject.length === 0) {
    errors.thank_you_email_subject = "Thank you email subject is required";
  }
  // Decision notification validation
  if (config.decision_notifications.enabled) {
    if (config.decision_notifications.from_email.length === 0) {
      errors.decision_notifications_from_email = "Decision notifications from email is required";
    }
    if (config.decision_notifications.from_name.length === 0) {
      errors.decision_notifications_from_name = "Decision notifications from name is required";
    }
    if (config.decision_notifications.subject_line.length === 0) {
      errors.decision_notifications_subject_line = "Decision notifications subject line is required";
    }
  }
  return { valid: Object.keys(errors).length === 0, errors: errors };
}

export type ActionConfig = Safe.TypeIn<typeof ActionConfigSerializer>;
export type ActionCustomData = Safe.TypeIn<typeof ActionFrontendDataSerializer>;
export type DecisionNotificationConfig = Safe.TypeIn<typeof DecisionNotificationConfigSerializer>;
